import { useCallback, useEffect, useMemo, useState } from 'react'
import { caseService } from '../../api/services/case.service'
import { outcomeService } from '../../api/services/outcome.service'
import {
  buildCaseFilesFormData,
  CaseMediaUploader,
  type PendingCaseMedia,
} from './CaseMediaUploader'
import { CaseFilesGallery } from './CaseFilesGallery'
import { Button } from '../ui/button'
import { FieldLabel } from '../ui/field-label'
import { MultiSelectPicker } from '../ui/multi-select-picker'
import { SelectPicker } from '../ui/select-picker'
import type { Case, CaseFile, NamedRef } from '../../types/case'
import {
  CASE_OUTCOME_RESULT_LABELS,
  CASE_OUTCOME_RESULTS,
  type CaseOutcomePayload,
  type CaseOutcomeResult,
} from '../../types/outcome'

type CaseOutcomePanelProps = {
  caseItem: Case
  files: CaseFile[]
  canView: boolean
  canUpdate: boolean
  canUploadFiles: boolean
  canDownloadFiles: boolean
  canDeleteFiles: boolean
  actionLoading: boolean
  onFilesChanged: () => Promise<void> | void
}

function toSelectOptions(items: NamedRef[]) {
  return items.map((item) => ({ value: item.id, label: item.name }))
}

export function CaseOutcomePanel({
  caseItem,
  files,
  canView,
  canUpdate,
  canUploadFiles,
  canDownloadFiles,
  canDeleteFiles,
  actionLoading,
  onFilesChanged,
}: CaseOutcomePanelProps) {
  const [payload, setPayload] = useState<CaseOutcomePayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<CaseOutcomeResult | ''>('')
  const [notes, setNotes] = useState('')
  const [furtherComplicationIds, setFurtherComplicationIds] = useState<string[]>([])
  const [complicationOptions, setComplicationOptions] = useState<
    Array<{ value: string; label: string }>
  >([])
  const [pendingMedia, setPendingMedia] = useState<PendingCaseMedia[]>([])

  const outcomeFiles = useMemo(
    () => files.filter((file) => file.type === 'outcome'),
    [files],
  )

  const isDraft = caseItem.status === 'draft'
  const isArchived = caseItem.is_archived || Boolean(caseItem.deleted_at)
  const formDisabled = !canUpdate || isDraft || isArchived || saving || actionLoading

  const loadOutcome = useCallback(async () => {
    if (!canView) {
      setPayload(null)
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      const response = await outcomeService.show(caseItem.id)
      const data = response.data
      setPayload(data)
      setResult(data.result ?? '')
      setNotes(data.notes ?? '')
      setFurtherComplicationIds(data.further_complications.map((item) => item.id))
    } catch {
      setPayload(null)
    } finally {
      setLoading(false)
    }
  }, [canView, caseItem.id])

  useEffect(() => {
    void loadOutcome()
  }, [loadOutcome])

  useEffect(() => {
    if (!canUpdate) {
      return
    }

    void caseService
      .filters()
      .then((response) => {
        setComplicationOptions(toSelectOptions(response.data.complications ?? []))
      })
      .catch(() => {
        setComplicationOptions([])
      })
  }, [canUpdate])

  const handleSave = async () => {
    if (!result || formDisabled) {
      return
    }

    setSaving(true)

    try {
      const response = await outcomeService.update(caseItem.id, {
        result,
        notes: notes.trim() || null,
        further_complication_ids: furtherComplicationIds,
      })
      setPayload(response.data)
      setResult(response.data.result ?? '')
      setNotes(response.data.notes ?? '')
      setFurtherComplicationIds(response.data.further_complications.map((item) => item.id))
    } catch {
      // toasted globally
    } finally {
      setSaving(false)
    }
  }

  const handleUpload = async (items: PendingCaseMedia[]) => {
    if (items.length === 0 || !canUploadFiles) {
      return
    }

    setSaving(true)

    try {
      await caseService.uploadFiles(caseItem.id, buildCaseFilesFormData(items))
      setPendingMedia([])
      await onFilesChanged()
    } catch {
      // toasted globally
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteFile = (fileId: string) => {
    void (async () => {
      try {
        await caseService.deleteFile(caseItem.id, fileId)
        await onFilesChanged()
      } catch {
        // toasted globally
      }
    })()
  }

  const handleDownloadFile = async (fileId: string) => {
    try {
      const response = await caseService.downloadFile(caseItem.id, fileId)
      window.open(response.data.url, '_blank', 'noopener,noreferrer')
    } catch {
      // toasted globally
    }
  }

  if (!canView) {
    return (
      <p className="text-sm text-ink-500">
        You do not have permission to view the post-procedure outcome.
      </p>
    )
  }

  if (loading) {
    return <p className="text-sm text-ink-500">Loading outcome…</p>
  }

  const resultOptions = CASE_OUTCOME_RESULTS.map((value) => ({
    value,
    label: CASE_OUTCOME_RESULT_LABELS[value],
  }))

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-semibold">Post-procedure outcome</h2>
        <p className="mt-1 text-sm text-ink-600">
          Record the structured outcome after the procedure. This is separate from the
          free-text outcome that becomes available on Edit once the case is published and
          the procedure date has passed.
        </p>
      </div>

      {payload?.is_due && (
        <div className="rounded-md border border-gold-200 bg-gold-lt px-4 py-3 text-sm text-gold">
          Outcome update is due
          {payload.due_at ? ` (due ${payload.due_at})` : ''}. Please complete the
          post-procedure result.
        </div>
      )}

      {isDraft && (
        <div className="rounded-md border border-parch-200 bg-parch-50 px-4 py-3 text-sm text-ink-600">
          Draft cases cannot receive a post-procedure outcome. Publish or schedule the
          case first.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-ink-400">Status</p>
          <p className="mt-1 text-sm font-medium text-ink-800 capitalize">
            {payload?.status ?? 'pending'}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-ink-400">
            Completed at
          </p>
          <p className="mt-1 text-sm text-ink-800">
            {payload?.completed_at
              ? new Date(payload.completed_at).toLocaleString()
              : '—'}
          </p>
        </div>
      </div>

      <div className="space-y-4 rounded-md border border-parch-100 p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel required>Result</FieldLabel>
            <SelectPicker
              options={resultOptions}
              value={result}
              onChange={(value) => {
                if (!formDisabled) {
                  setResult(value as CaseOutcomeResult)
                }
              }}
              placeholder="Select result"
            />
          </div>

          <div>
            <FieldLabel optional>Further complications</FieldLabel>
            <MultiSelectPicker
              options={complicationOptions}
              values={furtherComplicationIds}
              onChange={(values) => {
                if (!formDisabled) {
                  setFurtherComplicationIds(values)
                }
              }}
              placeholder="Select further complications"
            />
          </div>
        </div>

        <div>
          <FieldLabel optional>Description</FieldLabel>
          <textarea
            className="min-h-24 w-full rounded-md border border-parch-200 px-3 py-2 text-sm disabled:bg-parch-50"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={formDisabled}
            placeholder="Post-procedure description…"
          />
        </div>

        {canUpdate && (
          <Button
            size="sm"
            disabled={formDisabled || !result}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save Outcome'}
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="font-display text-base font-semibold text-ink-900">
          Outcome files
        </h3>
        <CaseFilesGallery
          files={outcomeFiles}
          actionLoading={actionLoading || saving}
          canDownload={canDownloadFiles}
          canDelete={canDeleteFiles && canUpdate && !isArchived}
          onDownload={(fileId) => void handleDownloadFile(fileId)}
          onDelete={handleDeleteFile}
        />

        {canUploadFiles && canUpdate && !isDraft && !isArchived && (
          <CaseMediaUploader
            files={pendingMedia}
            onChange={setPendingMedia}
            disabled={actionLoading || saving}
            uploading={saving}
            onUpload={handleUpload}
            title="Upload outcome files"
            allowedTypes={['outcome']}
          />
        )}
      </div>
    </div>
  )
}
